import axios from 'axios';
import { handleAuthError } from '../funciones/authHelper';
import { getCachedData, invalidateCacheByPrefix, CACHE_KEYS } from '../funciones/apiCache';

const API_URL = process.env.REACT_APP_API_URL;

const CACHE_TTL = {
    DASHBOARD: 250 * 1000,
    SOLICITUDES: 250 * 1000,
    CALENDARIO: 250 * 1000
};

// Ámbito de vacaciones del usuario, resuelto en SERVIDOR (GET /vacaciones/mi-ambito).
// Sustituye el cálculo del ámbito desde el token (que solo se refrescaba al re-loguear):
// conceder/revocar surten efecto sin re-login. SIN caché a propósito — es barato y debe
// reflejar el estado actual. Forma: { puedeAccederModulo, alcanceTotal, esSupervisor,
// ver:{ todosNegocios, todasColaboradoras, negocios:[{id,nombre}], colaboradoras:[{id,nombre}] },
// gestionar:{ ...igual } }.
export const fetchMiAmbito = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/vacaciones/mi-ambito`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`,
                'Cache-Control': 'no-cache'
            }
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

// --- Panel de ámbito de vacaciones (Fase 4.1/4.2). Gateados por vacaciones.ambito.configurar. ---

// Toda la configuración de la pantalla en una llamada: { beneficiarios, catalogos }.
export const fetchConfiguracionAmbito = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/vacaciones/ambito/configuracion`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`,
                'Cache-Control': 'no-cache'
            }
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

// Recuento de configuraciones HIPOTÉTICAS (no escribe). configuraciones: [{ ref, ver, gestionar }].
export const calcularAlcanceAmbito = async (configuraciones) => {
    try {
        const response = await axios.post(`${API_URL}/api/vacaciones/ambito/alcance`, { configuraciones }, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        return response.data; // { resultados: [{ ref, alcance, alcanceGestionar }] }
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

// Full-replace del ámbito de un beneficiario. cuerpo: { ver:{...}, gestionar:{...} }.
export const guardarAmbitoBeneficiario = async (tipoBeneficiario, id, cuerpo) => {
    try {
        const response = await axios.put(
            `${API_URL}/api/vacaciones/ambito/${tipoBeneficiario}/${encodeURIComponent(id)}`,
            cuerpo,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem("token")}` } }
        );
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

// Revoca (borra todas las filas del beneficiario).
export const revocarAmbitoBeneficiario = async (tipoBeneficiario, id) => {
    try {
        const response = await axios.delete(
            `${API_URL}/api/vacaciones/ambito/${tipoBeneficiario}/${encodeURIComponent(id)}`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem("token")}` } }
        );
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchAllSolicitudes = async (params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/vacaciones/solicitudes`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            },
            params
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchExportarSolicitudes = async (params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/vacaciones/solicitudes/exportar`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            },
            params,
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchExportarCalendario = async (params = {}) => {
    try {
        const response = await axios.get(`${API_URL}/api/vacaciones/calendario/exportar`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            },
            params,
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchMisSolicitudes = async (anio = null, forceRefresh = false) => {
    const cacheKey = `${CACHE_KEYS.MIS_SOLICITUDES}_${anio || 'current'}`;

    const fetchFn = async () => {
        const params = {};
        if (anio) params.anio = anio;

        const response = await axios.get(`${API_URL}/api/vacaciones/mis-solicitudes`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            },
            params
        });
        return response.data;
    };

    try {
        if (forceRefresh) {
            invalidateCacheByPrefix(CACHE_KEYS.MIS_SOLICITUDES);
        }
        return await getCachedData(cacheKey, fetchFn, CACHE_TTL.SOLICITUDES);
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const crearSolicitud = async (solicitudData) => {
    try {
        const response = await axios.post(`${API_URL}/api/vacaciones/solicitud`, solicitudData, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        invalidateCacheByPrefix(CACHE_KEYS.DASHBOARD_STATS);
        invalidateCacheByPrefix(CACHE_KEYS.MIS_SOLICITUDES);
        invalidateCacheByPrefix(CACHE_KEYS.CALENDARIO);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const aprobarSolicitud = async (id, comentario = '') => {
    try {
        const response = await axios.post(`${API_URL}/api/vacaciones/solicitud/${id}/aprobar`,
            { comentario },
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem("token")}`
                }
            }
        );
        invalidateCacheByPrefix(CACHE_KEYS.DASHBOARD_STATS);
        invalidateCacheByPrefix(CACHE_KEYS.MIS_SOLICITUDES);
        invalidateCacheByPrefix(CACHE_KEYS.CALENDARIO);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const rechazarSolicitud = async (id, motivo_rechazo) => {
    try {
        const response = await axios.post(`${API_URL}/api/vacaciones/solicitud/${id}/rechazar`,
            { motivo_rechazo },
            {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem("token")}`
                }
            }
        );
        invalidateCacheByPrefix(CACHE_KEYS.DASHBOARD_STATS);
        invalidateCacheByPrefix(CACHE_KEYS.MIS_SOLICITUDES);
        invalidateCacheByPrefix(CACHE_KEYS.CALENDARIO);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchCalendarioVacaciones = async (params = {}, forceRefresh = false) => {
    const cacheKey = `${CACHE_KEYS.CALENDARIO}_${params.mes || ''}_${params.anio || ''}_${params.vista || ''}_${params.estado || ''}_${params.empresa_id || ''}_${params.departamento_id || ''}_${params.persona_id || ''}_${params.empresa_ids || ''}_${params.departamento_ids || ''}_${params.persona_ids || ''}_${params.colaborador || ''}_${params.gestion_vacaciones_int || ''}_${params.gestion_vacaciones_colab || ''}`;

    const fetchFn = async () => {
        const response = await axios.get(`${API_URL}/api/vacaciones/calendario`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            },
            params
        });
        return response.data;
    };

    try {
        if (forceRefresh) {
            invalidateCacheByPrefix(CACHE_KEYS.CALENDARIO);
        }
        return await getCachedData(cacheKey, fetchFn, CACHE_TTL.CALENDARIO);
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchFestivosPersonas = async (params = {}, forceRefresh = false) => {
    const cacheKey = `${CACHE_KEYS.CALENDARIO}_festp_${params.anio || ''}_${params.vista || ''}_${params.empresa_ids || ''}_${params.departamento_ids || ''}_${params.persona_ids || ''}_${params.colaborador || ''}_${params.gestion_vacaciones_int || ''}_${params.gestion_vacaciones_int_empresas || ''}_${params.gestion_vacaciones_colab || ''}_${params.admin_todas || ''}_${params.excluir_calendario_id || ''}`;

    const fetchFn = async () => {
        const response = await axios.get(`${API_URL}/api/vacaciones/calendario/festivos-personas`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            },
            params
        });
        return response.data;
    };

    try {
        if (forceRefresh) {
            invalidateCacheByPrefix(CACHE_KEYS.CALENDARIO);
        }
        return await getCachedData(cacheKey, fetchFn, CACHE_TTL.CALENDARIO);
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchDashboardStats = async (forceRefresh = false) => {
    const fetchFn = async () => {
        const response = await axios.get(`${API_URL}/api/vacaciones/dashboard`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        return response.data;
    };

    try {
        if (forceRefresh) {
            invalidateCacheByPrefix(CACHE_KEYS.DASHBOARD_STATS);
        }
        return await getCachedData(CACHE_KEYS.DASHBOARD_STATS, fetchFn, CACHE_TTL.DASHBOARD);
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const eliminarSolicitud = async (id) => {
    try {
        const response = await axios.delete(`${API_URL}/api/vacaciones/solicitud/${id}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        invalidateCacheByPrefix(CACHE_KEYS.DASHBOARD_STATS);
        invalidateCacheByPrefix(CACHE_KEYS.MIS_SOLICITUDES);
        invalidateCacheByPrefix(CACHE_KEYS.CALENDARIO);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const actualizarSolicitud = async (id, solicitudData) => {
    try {
        const response = await axios.put(`${API_URL}/api/vacaciones/solicitud/${id}`, solicitudData, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        invalidateCacheByPrefix(CACHE_KEYS.DASHBOARD_STATS);
        invalidateCacheByPrefix(CACHE_KEYS.MIS_SOLICITUDES);
        invalidateCacheByPrefix(CACHE_KEYS.CALENDARIO);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchPersonasVacaciones = async (params = {}) => {
    try {
        const response = await axios.post(`${API_URL}/api/vacaciones/personas`, params, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

// Checkpoint de DOS llamadas: sin `recuentoConfirmado` = PREVIEW (el servidor devuelve
// {recuento, fuera_de_ambito} y no escribe); con él = APLICAR (viaja el recuento que el humano
// confirmó, y el servidor re-verifica que sigue coincidiendo antes de escribir).
export const asignarCalendarioLaboralMasivo = async (personaIds, calendarioLaboralId, tipo = 'interno', recuentoConfirmado = null) => {
    try {
        const body = { persona_ids: personaIds, calendario_laboral_id: calendarioLaboralId, tipo };
        if (recuentoConfirmado !== null && recuentoConfirmado !== undefined) {
            body.recuento_confirmado = recuentoConfirmado;
        }
        const response = await axios.post(`${API_URL}/api/vacaciones/personas/asignar-calendario`, body, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};
